import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Send, Plus, Trash2, MessageSquare, Loader2,
  ChevronLeft, ChevronRight, Sparkles, Database,
  Zap, TrendingUp, Users, BarChart3, RefreshCw,
} from 'lucide-react';
import MarkdownMessage from '../../../components/ui/markdown-message';
import MessageActions from '../../../components/ui/MessageActions';
import MemoModal from '../../../components/ui/MemoModal';
import CommentThread from '../../../components/ui/CommentThread';
import { useYenomAIStore } from '../../../state/yenomAIStore';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

const TOOL_LABELS = {
  search_companies: 'Searching companies',
  get_company_profile: 'Loading company profile',
  get_financials: 'Fetching financials',
  get_market_snapshot: 'Checking market data',
  get_financial_ratios: 'Analysing ratios',
  get_shareholding: 'Reviewing shareholding',
  get_insider_trades: 'Checking insider trades',
  get_corporate_actions: 'Loading corporate actions',
  get_directors: 'Reviewing board',
  get_x_signals: 'Scanning X signals',
  search_filings: 'Searching filings',
  get_bse_notices: 'Checking BSE notices',
  get_company_charges: 'Loading charges',
  compare_peers: 'Comparing peers',
  screen_universe: 'Screening universe',
  get_portfolio_holdings: 'Reading portfolio',
  get_recent_material_updates: 'Checking recent updates',
  get_index_snapshot: 'Checking index levels',
  get_market_pulse: 'Reading market pulse',
  get_regulator_announcements: 'Reviewing RBI / SEBI announcements',
  get_market_news: 'Scanning market news',
  get_daily_fund_flows: 'Pulling FII / DII flows',
  list_sectors: 'Listing sectors',
  get_sector_briefing: 'Building sector briefing',
  compare_sectors: 'Comparing sectors',
  get_sector_news: 'Scanning sector news',
  get_sector_fund_flows: 'Tracking sector fund flows',
  web_search: 'Searching the web',
  search_x_live: 'Searching X live',
};

const SUGGESTED = [
  'How did Indian markets close today?',
  'Give me a briefing on the Banking sector',
  'FII / DII flows over the last 5 sessions',
  "What did RBI announce this month?",
  "What's new with Reliance this month?",
  'Compare HDFC Bank vs ICICI Bank on ROE, PE, and market cap',
];

function ToolPills({ tools }) {
  if (!tools || tools.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {tools.map((t, i) => (
        <span
          key={`${t.name}-${i}`}
          className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium transition-colors ${
            t.status === 'running'
              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {t.status === 'running' ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Database className="w-3 h-3" />
          )}
          {TOOL_LABELS[t.name] || t.name}
        </span>
      ))}
    </div>
  );
}

const SIGNAL_META = {
  insider_accumulation: { label: 'Insider Buying', icon: TrendingUp, color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  promoter_increase: { label: 'Promoter Increase', icon: Users, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  fii_dii_convergence: { label: 'FII+DII Buying', icon: BarChart3, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  valuation_compression: { label: 'Value Zone', icon: Zap, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

function SignalsPanel({ onAskAbout }) {
  const { signals, signalsLoading, fetchSignals, runScan } = useYenomAIStore();
  const [filter, setFilter] = useState(null);

  useEffect(() => { fetchSignals(filter); }, [fetchSignals, filter]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Signals
          </h2>
          <p className="text-xs text-muted-foreground">Proactive alerts from market pattern scanning</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={runScan}>
          <RefreshCw className="w-3 h-3" /> Scan Now
        </Button>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        <Badge
          variant={filter === null ? 'default' : 'outline'}
          className="cursor-pointer text-xs"
          onClick={() => setFilter(null)}
        >All</Badge>
        {Object.entries(SIGNAL_META).map(([key, meta]) => (
          <Badge
            key={key}
            variant={filter === key ? 'default' : 'outline'}
            className="cursor-pointer text-xs"
            onClick={() => setFilter(key)}
          >{meta.label}</Badge>
        ))}
      </div>

      {signalsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : signals.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No signals detected yet. Run a scan to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {signals.map(s => {
            const meta = SIGNAL_META[s.signal_type] || { label: s.signal_type, icon: Zap, color: 'bg-muted text-muted-foreground' };
            const Icon = meta.icon;
            return (
              <Card key={s.id} className="hover:bg-muted/30 transition-colors">
                <CardContent className="p-3 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      <span className="font-semibold text-sm truncate">{s.company_name}</span>
                      <span className="text-xs text-muted-foreground">{s.nse_symbol}</span>
                    </div>
                    {s.conviction_score != null && (
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        Conviction: {(s.conviction_score * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {s.ai_narrative && (
                    <p className="text-xs text-muted-foreground leading-relaxed">{s.ai_narrative}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(s.detected_at).toLocaleDateString()}
                    </span>
                    <Button
                      variant="ghost" size="sm" className="h-6 text-[11px] gap-1 text-emerald-600"
                      onClick={() => onAskAbout(s.company_name, s.isin)}
                    >
                      <Sparkles className="w-3 h-3" /> Ask AI
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function stripMarkdown(md) {
  return (md || '')
    .replace(/^\|.*\|$/gm, '')      // table rows
    .replace(/^[-|: ]+$/gm, '')     // table separators
    .replace(/[#*_~`>]/g, '')       // inline formatting
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function MessageBubble({ msg, userQuery, onExportPdf, onGenerateMemo }) {
  const isUser = msg.role === 'user';
  const contentRef = useRef(null);
  return (
    <div className={`group flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] md:max-w-[75%] ${isUser ? '' : 'space-y-0'}`}>
        <div
          ref={contentRef}
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-emerald-600 text-white'
              : 'bg-muted/50 text-foreground'
          }`}
        >
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <div className="message-content">
              <MarkdownMessage content={msg.content || ''} />
            </div>
          )}
        </div>
        {!isUser && msg.content && (
          <MessageActions
            content={msg.content}
            contentRef={contentRef}
            onExportPdf={onExportPdf}
            onGenerateMemo={onGenerateMemo}
            sharePayload={{
              messageType: 'shared_ai',
              type: 'ai_response',
              title: userQuery || 'AI Analysis',
              summary: stripMarkdown(msg.content).slice(0, 200),
              full_content: msg.content,
              query: userQuery || '',
            }}
          />
        )}
      </div>
    </div>
  );
}

export default function YenomAIPage() {
  const [searchParams] = useSearchParams();
  const contextIsin = searchParams.get('isin');
  const contextSymbol = searchParams.get('symbol');
  const promptParam = searchParams.get('prompt');

  const {
    conversations, conversationsLoading, activeConversationId,
    messages, streaming, error, toolStatus,
    fetchConversations, loadConversation, deleteConversation,
    sendMessage, newConversation,
  } = useYenomAIStore();

  const [input, setInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const promptSentRef = useRef(false);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);


  useEffect(() => {
    if (promptParam && !promptSentRef.current && !streaming) {
      promptSentRef.current = true;
      newConversation();
      setTimeout(() => {
        sendMessage(promptParam, contextIsin ? { isin: contextIsin, symbol: contextSymbol } : undefined);
      }, 100);
    }
  }, [promptParam, streaming, newConversation, sendMessage, contextIsin, contextSymbol]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, toolStatus]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    const ctx = contextIsin ? { isin: contextIsin, symbol: contextSymbol } : undefined;
    sendMessage(text, ctx);
  }, [input, streaming, contextIsin, contextSymbol, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestion = (text) => {
    setInput('');
    setActiveTab('chat');
    const ctx = contextIsin ? { isin: contextIsin, symbol: contextSymbol } : undefined;
    sendMessage(text, ctx);
  };

  const handleAskAboutSignal = (companyName, isin) => {
    setActiveTab('chat');
    newConversation();
    sendMessage(`Tell me about ${companyName} — why are signals firing for this stock? Analyze the recent data.`, { isin });
  };

  const handleExportPdf = useCallback((contentRef) => {
    if (!contentRef?.current) return;
    import('../../../utils/exportPdf').then(({ exportElementAsPdf }) => {
      exportElementAsPdf(contentRef.current);
    });
  }, []);

  const [memoOpen, setMemoOpen] = useState(false);
  const handleOpenMemo = useCallback(() => setMemoOpen(true), []);

  const isEmpty = messages.length === 0 && !streaming;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0 md:w-12'
        } transition-all duration-200 flex-shrink-0 border-r border-border bg-background overflow-hidden flex flex-col`}
      >
        <div className={`p-2 flex items-center ${sidebarOpen ? 'justify-between' : 'justify-center'}`}>
          {sidebarOpen && (
            <Button variant="ghost" size="sm" className="gap-1 text-xs" onClick={() => { newConversation(); }}>
              <Plus className="w-3.5 h-3.5" /> New
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>

        {sidebarOpen && (
          <div className="flex-1 overflow-y-auto px-1 pb-2 space-y-0.5">
            {conversationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : conversations.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No conversations yet</p>
            ) : (
              conversations.map(c => (
                <div
                  key={c.id}
                  className={`group flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer text-xs hover:bg-muted/50 ${
                    c.id === activeConversationId ? 'bg-muted' : ''
                  }`}
                  onClick={() => loadConversation(c.id)}
                >
                  <MessageSquare className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1">{c.title || c.preview || 'Untitled'}</span>
                  <button
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-500 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Tab bar */}
        <div className="border-b border-border px-4 flex gap-0">
          {[
            { key: 'chat', label: 'Chat', icon: MessageSquare },
            { key: 'signals', label: 'Signals', icon: Zap },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'signals' ? (
          <div className="flex-1 overflow-y-auto">
            <SignalsPanel onAskAbout={handleAskAboutSignal} />
          </div>
        ) : (
        <>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-8 h-8 text-emerald-500" />
                <h1 className="text-2xl font-bold tracking-tight">KubAIr</h1>
              </div>
              <p className="text-sm text-muted-foreground text-center max-w-md mb-8">
                Ask anything about Indian public companies. I have access to financials, shareholding,
                insider trades, X signals, filings, and more.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl w-full">
                {SUGGESTED.map((s, i) => (
                  <Card
                    key={i}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleSuggestion(s)}
                  >
                    <CardContent className="p-3">
                      <p className="text-xs text-muted-foreground leading-relaxed">{s}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
              {messages.map((msg, i) => {
                if (msg.role === 'assistant' && (!msg.content || msg.content.trim() === '')) return null;
                const prevUser = msg.role === 'assistant'
                  ? [...messages.slice(0, i)].reverse().find(m => m.role === 'user')
                  : null;
                return (
                  <MessageBubble
                    key={msg.id || i}
                    msg={msg}
                    userQuery={prevUser?.content}
                    onExportPdf={handleExportPdf}
                    onGenerateMemo={messages.length > 1 ? handleOpenMemo : undefined}
                  />
                );
              })}

              {streaming && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3 bg-muted/50 space-y-2">
                    {toolStatus.length > 0 && <ToolPills tools={toolStatus} />}
                    <div className="flex gap-1.5 items-center text-muted-foreground">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />
                      <span className="text-xs">
                        {toolStatus.some(t => t.status === 'running')
                          ? 'Working...'
                          : 'Thinking...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mb-2 p-2 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 text-xs rounded-lg">
            {error}
          </div>
        )}

        {/* Comments */}
        {activeConversationId && messages.length > 0 && (
          <div className="mx-4 mb-2">
            <CommentThread entityType="conversation" entityId={activeConversationId} />
          </div>
        )}

        {/* Input */}
        <div className="border-t border-border p-3 bg-background">
          <div className="max-w-3xl mx-auto flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about any Indian listed company..."
              rows={1}
              className="flex-1 resize-none rounded-xl border border-border bg-muted/30 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 placeholder:text-muted-foreground"
              style={{ minHeight: '42px', maxHeight: '120px' }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            <Button
              size="icon"
              className="h-[42px] w-[42px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
              disabled={!input.trim() || streaming}
              onClick={handleSend}
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-1.5">
            KubAIr can make mistakes. Not investment advice.
          </p>
        </div>
        </>
        )}
      </div>

      <MemoModal
        open={memoOpen}
        onClose={() => setMemoOpen(false)}
        conversationId={activeConversationId}
      />
    </div>
  );
}
